import { formatDate, calculatePendingAgeing, getAgeingColor, getLocalToday } from "../../utils/helpers";
import { getCallStatusColor } from "../../utils/callStatusOptions";
import { getJoStatusColor } from "../../utils/joStatusColors";
import { getCompanyBadgeClass } from "../../utils/companyColors";
import NapCodeMapLinks from "../common/NapCodeMapLinks";

export const ALL_COLUMNS = [
  { key: "count", label: "Count", sortable: false, hideable: false },
  { key: "chat", label: "Chat", sortable: false, hideable: false },
  { key: "job_date", label: "Dispatch Date", render: (v) => formatDate(v), hideable: true },
  { key: "gofiber_status", label: "GoFiber Status", render: (v) => v === "For Schedule of Installation" ? "FSOI" : (v || "-"), hideable: false },
  { key: "jo_number", label: "JO Number", render: (v) => v || "-", hideable: false },
  { key: "team_number", label: "Team Number", hideable: true },
  { key: "personnel_names", label: "Personnel", hideable: true },
  { key: "company_name", label: "Company", render: (v) => v || "-", hideable: false },
  { key: "assigned_tl_name", label: "TL", minWidth: "100px", hideable: true },
  { key: "account_number", label: "Account Number", hideable: true },
  { key: "reference_id", label: "Reference ID", render: (v) => v || "-", hideable: true },
  { key: "municipality", label: "Municipality", hideable: true },
  { key: "barangay", label: "Barangay", hideable: true },
  { key: "customer_full_name", label: "Customer Name", hideable: true },
  { key: "jo_status", label: "Status", hideable: false },
  { key: "jo_status_remarks", label: "Status Remarks", hideable: true },
  { key: "nap_code", label: "NAP Code", hideable: true, defaultHidden: true },
  { key: "serial_number", label: "Modem Serial Number", hideable: true },
  { key: "call_status", label: "Call Status", hideable: false },
  { key: "call_status_remarks", label: "Call Remarks", hideable: true },
  { key: "customer_mobile_number", label: "Mobile Number", hideable: true },
  { key: "complete_address", label: "Address", hideable: true },
  { key: "updated_by_name", label: "Updated By", hideable: true },
];

export default function JobOrderTable({
  items,
  sortStack,
  onSortClick,
  onRowClick,
  onStatusClick,
  onCallStatusClick,
  onTeamClick,
  onCompanyClick,
  onCustomerLocationClick,
  userCompanyId,
  canEditCrossCompanyTeam = false,
  scrollRef,
  onScroll,
  loadingMore,
  canViewChat,
  onChatClick,
  hiddenColumns,
  canEditTerminalTeam = false,
}) {
  const hidden = hiddenColumns || new Set();

  const visibleColumns = ALL_COLUMNS.filter((col) => {
    if (col.key === "chat" && !canViewChat) return false;
    if (hidden.has(col.key)) return false;
    return true;
  });

  const sortIndex = (key) => {
    if (!sortStack) return -1;
    return sortStack.findIndex((s) => s.key === key);
  };

  const sortEntry = (key) => {
    const idx = sortIndex(key);
    return idx >= 0 ? sortStack[idx] : null;
  };

  const renderSortIndicator = (col) => {
    if (col.sortable === false) return null;
    const entry = sortEntry(col.key);
    if (!entry) return <span className="text-gray-300 ml-1 text-xs">⇅</span>;
    const idx = sortIndex(col.key);
    const arrow = entry.dir === "asc" ? "▲" : "▼";
    const badge = sortStack.length > 1 ? (
      <sup className="text-[0.6em] ml-0.5 font-bold text-blue-600">{idx + 1}</sup>
    ) : null;
    return <span className="text-blue-600 ml-1 text-xs">{arrow}{badge}</span>;
  };

  return (
    <div className="flex-1 flex flex-col min-h-0 border rounded-lg overflow-hidden">
      <div className="flex-1 min-h-0 overflow-x-auto" ref={scrollRef} onScroll={onScroll} style={{ overflowY: "auto" }}>
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 text-left sticky top-0 z-20">
            <tr>
              {visibleColumns.map((col) => {
                const isFrozen = col.key === "count";
                return (
                <th
                  key={col.key}
                  onClick={(e) => col.sortable !== false && onSortClick(col.key, e)}
                  className={`px-3 py-2 font-semibold text-gray-700 whitespace-nowrap bg-gray-50 ${
                    col.sortable !== false ? "cursor-pointer select-none hover:bg-gray-100" : ""
                  }${isFrozen ? " sticky top-0 left-0 z-30 border-r border-gray-200" : ""}`}
                  style={col.minWidth ? { minWidth: col.minWidth } : undefined}
                  title={col.sortable !== false ? "Click to sort · Shift+Click for multi-sort" : undefined}
                >
                  <span className="inline-flex items-center">
                    {col.label}
                    {renderSortIndicator(col)}
                  </span>
                </th>
                );
              })}
            </tr>
          </thead>
          <tbody className="divide-y bg-white">
          {items.map((jo, idx) => (
            <tr
              key={jo.is_history ? `h-${jo.history_id}` : `a-${jo.id}`}
              onClick={() => onRowClick(jo)}
              className="bg-white hover:bg-blue-50 cursor-pointer"
            >
              {visibleColumns.map((col) => {
                if (col.key === "count") {
                  return (
                    <td key={col.key} className="px-3 py-2 whitespace-nowrap text-center text-gray-700 font-medium sticky left-0 z-10 bg-inherit border-r border-gray-200">
                      {idx + 1}
                    </td>
                  );
                }

                if (col.key === "chat") {
                  const count = jo.chat_count || 0;
                  const hasUnread = jo.has_unread_chat;
                  const displayCount = hasUnread ? (jo.unread_count || 0) : count;
                  return (
                    <td key={col.key} className="px-3 py-2 whitespace-nowrap text-center">
                      <button
                        onClick={(e) => { e.stopPropagation(); onChatClick && onChatClick(jo); }}
                        className={`inline-flex items-center justify-center min-w-[28px] h-7 px-1.5 rounded-full text-xs font-bold transition-colors ${
                          count === 0
                            ? "bg-gray-100 text-gray-400"
                            : hasUnread
                              ? "bg-red-100 text-red-700 font-extrabold ring-1 ring-red-300"
                              : "bg-green-100 text-green-700"
                        }`}
                        title={hasUnread ? "Unread messages" : count > 0 ? "All read" : "No messages"}
                      >
                        {displayCount}
                      </button>
                    </td>
                  );
                }

                const raw = jo[col.key];
                const display = col.render ? col.render(raw) : raw ?? "";

                if (col.key === "jo_status") {
                  const badge = getJoStatusColor(raw);
                  const ageing = calculatePendingAgeing(jo, getLocalToday());
                  const isTerminal = raw === "Completed" || raw === "Cancelled";
                  const canQuickAction = !isTerminal && !jo.is_history && onStatusClick;
                  return (
                    <td key={col.key} className="px-3 py-2 whitespace-nowrap">
                      <span
                        className={`px-2 py-0.5 rounded-full text-xs font-medium ${badge}${canQuickAction ? " cursor-pointer hover:ring-2 hover:ring-blue-300 hover:ring-offset-1 transition-shadow" : ""}`}
                        onClick={canQuickAction ? (e) => { e.stopPropagation(); onStatusClick(jo, e); } : undefined}
                      >
                        {display}
                        {ageing !== null && <sup className={`text-[0.65em] ml-0.5 font-bold ${getAgeingColor(ageing)}`}>{ageing}</sup>}
                      </span>
                    </td>
                  );
                }

                if (col.key === "call_status") {
                  const isTerminal = jo.jo_status === "Completed" || jo.jo_status === "Cancelled";
                  const canQuickCS = !isTerminal && !jo.is_history && onCallStatusClick;
                  const csDisplay = raw || "—";
                  const csBadge = raw ? getCallStatusColor(raw) : "bg-gray-100 text-gray-400";
                  return (
                    <td key={col.key} className="px-3 py-2 whitespace-nowrap">
                      <div className="max-w-[200px] truncate">
                        <span
                          className={`px-2 py-0.5 rounded-full text-xs font-medium ${csBadge}${canQuickCS ? " cursor-pointer hover:ring-2 hover:ring-blue-300 hover:ring-offset-1 transition-shadow" : ""}`}
                          onClick={canQuickCS ? (e) => { e.stopPropagation(); onCallStatusClick(jo, e); } : undefined}
                        >
                          {csDisplay}
                        </span>
                      </div>
                    </td>
                  );
                }

                if (col.key === "team_number") {
                  const isTerminal = jo.jo_status === "Completed" || jo.jo_status === "Cancelled";
                  // Locked when the team was first assigned by a different company...
                  const lockedByTeamOwner = jo.team_assigned_by_company && userCompanyId && jo.team_assigned_by_company !== userCompanyId;
                  // ...or when the JO has an assigned Company the current user does not belong to.
                  const lockedByCompany = jo.company_id && userCompanyId && jo.company_id !== userCompanyId;
                  // Cross-company permission holders bypass both locks.
                  const isLockedByOther = !canEditCrossCompanyTeam && (lockedByTeamOwner || lockedByCompany);
                  const terminalOverride = (isTerminal || jo.is_history) && canEditTerminalTeam;
                  const canQuickTeam = ((!isTerminal && !jo.is_history) || terminalOverride) && onTeamClick && !isLockedByOther;
                  const teamDisplay = raw || "—";
                  const lockTitle = lockedByCompany
                    ? "Locked — only users of the assigned company can edit the team number"
                    : "Locked — assigned by another company";
                  return (
                    <td key={col.key} className="px-3 py-2 whitespace-nowrap">
                      <span
                        className={`px-2 py-0.5 rounded text-xs font-medium ${
                          raw
                            ? "bg-indigo-50 text-indigo-700"
                            : "bg-gray-100 text-gray-400"
                        }${canQuickTeam ? " cursor-pointer hover:ring-2 hover:ring-indigo-300 hover:ring-offset-1 transition-shadow" : ""}${isLockedByOther ? " opacity-75" : ""}`}
                        onClick={canQuickTeam ? (e) => { e.stopPropagation(); onTeamClick(jo, e); } : undefined}
                        title={isLockedByOther ? lockTitle : undefined}
                      >
                        {teamDisplay}{isLockedByOther && raw ? " 🔒" : ""}
                      </span>
                    </td>
                  );
                }

                if (col.key === "company_name") {
                  const canQuickCompany = !jo.is_history && onCompanyClick;
                  const companyLabel = jo.company_prefix || raw;
                  return (
                    <td key={col.key} className="px-3 py-2 whitespace-nowrap">
                      <span
                        className={`px-2 py-0.5 rounded text-xs font-medium ${
                          getCompanyBadgeClass(companyLabel)
                        }${canQuickCompany ? " cursor-pointer hover:ring-2 hover:ring-sky-300 hover:ring-offset-1 transition-shadow" : ""}`}
                        onClick={canQuickCompany ? (e) => { e.stopPropagation(); onCompanyClick(jo, e); } : undefined}
                        title={canQuickCompany ? "Click to assign Company" : (raw || undefined)}
                      >
                        {companyLabel || "—"}
                      </span>
                    </td>
                  );
                }

                if (col.key === "customer_mobile_number") {
                  const mobile = (jo.customer_mobile_number || "").trim();
                  const alt = (jo.customer_alt_mobile_number || "").trim();
                  let merged = "";
                  if (mobile && alt && mobile !== alt) merged = `${mobile}; ${alt}`;
                  else merged = mobile || alt;
                  return (
                    <td key={col.key} className="px-3 py-2 whitespace-nowrap" title={merged}>
                      <div className="max-w-[200px] truncate">{merged}</div>
                    </td>
                  );
                }

                if (col.key === "gofiber_status" && raw === "Active") {
                  return (
                    <td key={col.key} className="px-3 py-2 whitespace-nowrap">
                      <span className="bg-green-100 text-green-700 font-semibold px-2 py-0.5 rounded">
                        {display}
                      </span>
                    </td>
                  );
                }

                if (col.key === "jo_number" && jo.jo_status === "Pending" && jo.has_reschedule_history) {
                  return (
                    <td key={col.key} className="px-3 py-2 whitespace-nowrap" title={String(display)}>
                      <div className="max-w-[200px] truncate">
                        <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded font-medium text-xs">
                          {display}
                        </span>
                      </div>
                    </td>
                  );
                }

                if (col.key === "nap_code") {
                  return (
                    <td key={col.key} className="px-3 py-2 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                      <div className="min-w-[160px] max-w-[220px]">
                        {raw ? <NapCodeMapLinks napCode={raw} /> : <span className="text-gray-400">—</span>}
                      </div>
                    </td>
                  );
                }

                if (col.key === "customer_full_name" && onCustomerLocationClick && display) {
                  return (
                    <td key={col.key} className="px-3 py-2 whitespace-nowrap" title={String(display)}>
                      <div className="max-w-[200px] truncate">
                        <button
                          onClick={(e) => { e.stopPropagation(); onCustomerLocationClick(jo); }}
                          className="text-blue-600 hover:text-blue-800 hover:underline font-medium text-left truncate max-w-[200px] transition-colors focus:outline-none focus:ring-1 focus:ring-blue-400 focus:ring-offset-1 rounded"
                          title={`Locate ${display} on map`}
                        >
                          {display}
                        </button>
                      </div>
                    </td>
                  );
                }

                return (
                  <td key={col.key} className="px-3 py-2 whitespace-nowrap" title={String(display)}>
                    <div className="max-w-[200px] truncate">
                      {display}
                    </div>
                  </td>
                );
              })}
            </tr>
          ))}
          {loadingMore && (
            <tr>
              <td colSpan={visibleColumns.length} className="text-center py-3 text-gray-400 text-xs">
                Loading more…
              </td>
            </tr>
          )}
        </tbody>
      </table>
      </div>
    </div>
  );
}
